import { useState } from "react";
import {
  Activity,
  KeyRound,
  ShieldCheck,
  CircleAlert,
  Settings2,
  Boxes,
  CheckCircle2,
  Plus,
  RefreshCw,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  Field,
  Select,
  SummaryMetrics,
  Checkbox,
  type MetricActionProps,
} from "../../design-system";
import {
  aiKeysFixture,
  withFixtureAiState,
  type AiExample,
  type AiKey,
  type AiKeysView,
  type AiKeysActions,
  type RelayReadiness,
} from "./llm-data";
import "./llm.css";

export const aiError = (e: unknown) =>
  e instanceof Error ? e.message : String(e);
export const usd = (value: number | null) =>
  value === null
    ? "단가 미설정"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(value);
export function RelayStatus({
  view,
  onSettings,
}: {
  view: RelayReadiness;
  onSettings?: () => void;
}) {
  return (
    <section
      className={`lai-readiness ${view.ok ? "is-ready" : "is-attention"}`}
      aria-label="API 키 중계 상태"
    >
      <span className="lai-state-icon">
        {view.ok ? <ShieldCheck size={21} /> : <CircleAlert size={21} />}
      </span>
      <div>
        <div className="lai-inline">
          <strong>API 키 중계</strong>
          <Badge tone={view.ok ? "teal" : "amber"}>{view.reason}</Badge>
        </div>
        <p>
          {view.model
            ? `${view.model} · 추론 ${view.effort}`
            : "사용할 모델을 설정해 주세요"}{" "}
          · 실행과 디바이스 조작은 현장 PC에서 진행됩니다.
        </p>
      </div>
      {onSettings && (
        <Button variant="secondary" size="sm" onClick={onSettings}>
          <Settings2 size={15} />
          모델·단가 설정
        </Button>
      )}
    </section>
  );
}

type DialogMode = "" | "create" | "edit" | "models" | "delete";
export type AiKeySummaryMetricId =
  | "keys"
  | "models"
  | "default-model"
  | "usage";
export type AiKeySummaryActions = Partial<
  Record<AiKeySummaryMetricId, MetricActionProps>
>;
export type AiKeySettingsProps = {
  /** Navigation and read permissions belong to the caller, independently of canManage. */
  summaryActions?: AiKeySummaryActions;
  view?: AiKeysView;
  actions?: AiKeysActions;
  canManage?: boolean;
  example?: AiExample;
  initialCreate?: boolean;
  initialModels?: boolean;
};
/** Controlled server values stay authoritative. Omitting view enables local Storybook fixtures. */
export function AiKeySettings({
  view: provided,
  actions,
  summaryActions,
  canManage = true,
  example = "normal",
  initialCreate = false,
  initialModels = false,
}: AiKeySettingsProps) {
  const [fixture, setFixture] = useState(() => aiKeysFixture(example));
  const view = provided ?? fixture;
  const writable =
    canManage && example !== "readonly" && (!provided || !!actions);
  const [dialog, setDialog] = useState<DialogMode>(
    initialCreate && writable
      ? "create"
      : initialModels && view.keys.length
        ? "models"
        : "",
  );
  const [selected, setSelected] = useState<AiKey | null>(
    initialModels ? (view.keys[0] ?? null) : null,
  );
  const [checked, setChecked] = useState<string[]>(
    initialModels ? (view.keys[0]?.enabledModels ?? []) : [],
  );
  const [busy, setBusy] = useState("");
  const [error, setError] = useState<{ scope: string; message: string } | null>(
    null,
  );
  const [notice, setNotice] = useState("");
  const [modelNote, setModelNote] = useState("");
  const [label, setLabel] = useState("");
  const [secret, setSecret] = useState("");
  const [priority, setPriority] = useState("10");
  const [budget, setBudget] = useState("");
  const update = (change: (current: AiKeysView) => AiKeysView) =>
    setFixture((current) => withFixtureAiState(change(current)));
  const begin = (mode: DialogMode, key?: AiKey) => {
    setError(null);
    setSecret("");
    setModelNote("");
    setSelected(key ?? null);
    setChecked(key?.enabledModels ?? []);
    setLabel(key?.label ?? "");
    setPriority(String(key?.priority ?? 10));
    setBudget(
      key?.monthlyBudgetUsd == null ? "" : String(key.monthlyBudgetUsd),
    );
    setDialog(mode);
  };
  const close = () => {
    if (!busy) {
      setDialog("");
      setSecret("");
      setError(null);
    }
  };
  const mutate = async (
    scope: string,
    fn: () => Promise<void>,
    done?: () => void,
  ) => {
    if (busy || !writable) return;
    setBusy(scope);
    setError(null);
    setNotice("");
    try {
      if (provided && !actions)
        throw new Error("현재 변경을 저장할 수 없습니다.");
      await fn();
      await actions?.refresh();
      done?.();
    } catch (e) {
      setError({
        scope,
        message: secret ? aiError(e).split(secret).join("[숨김]") : aiError(e),
      });
    } finally {
      setBusy("");
    }
  };
  const saveKey = () =>
    void mutate(
      "dialog",
      async () => {
        if (
          !label.trim() ||
          !Number.isInteger(Number(priority)) ||
          Number(priority) < 0 ||
          (budget !== "" &&
            (!Number.isFinite(Number(budget)) || Number(budget) < 0))
        )
          throw new Error("이름, 0 이상의 우선순위와 월 예산을 확인해 주세요.");
        const input = {
          label: label.trim(),
          priority: Number(priority),
          monthlyBudgetUsd: budget === "" ? null : Number(budget),
        };
        if (dialog === "create") {
          if (secret.trim().length < 20)
            throw new Error("API 키를 확인해 주세요.");
          if (!provided && example === "error")
            throw new Error(
              "연결 확인 실패 · API 키를 확인하고 다시 시도해 주세요.",
            );
          const result = actions
            ? await actions.create({ ...input, secret })
            : await Promise.resolve({
                key: {
                  ...aiKeysFixture().keys[0],
                  ...input,
                  id: crypto.randomUUID(),
                  hint: "sk-proj…TEST",
                  enabledModels: [],
                  month: { costUsd: 0, calls: 0, tokens: 0 },
                  lastUsedAt: null,
                  lastError: null,
                } satisfies AiKey,
                note: "",
              });
          if (!provided)
            update((current) => ({
              ...current,
              keys: [...current.keys, result.key],
            }));
          setSecret("");
          setSelected(result.key);
          setChecked(result.key.enabledModels);
          setModelNote(result.note ?? "");
          setDialog("models");
          setNotice("키를 등록했습니다. 이 키로 사용할 모델을 골라 주세요.");
        } else if (selected) {
          await actions?.patch(selected.id, input);
          if (!provided)
            update((current) => ({
              ...current,
              keys: current.keys.map((key) =>
                key.id === selected.id ? { ...key, ...input } : key,
              ),
            }));
        }
      },
      dialog === "edit"
        ? () => {
            setDialog("");
            setNotice("키 설정을 저장했습니다.");
          }
        : undefined,
    );
  const saveModels = () =>
    selected &&
    void mutate(
      "dialog",
      async () => {
        await actions?.patch(selected.id, { enabledModels: checked });
        if (!provided)
          update((current) => ({
            ...current,
            keys: current.keys.map((key) =>
              key.id === selected.id ? { ...key, enabledModels: checked } : key,
            ),
          }));
      },
      () => {
        setDialog("");
        setNotice(
          "사용 모델을 저장했습니다. 아래에서 기본 모델을 확인해 주세요.",
        );
      },
    );
  const checkKey = (key: AiKey) =>
    void mutate(key.id, async () => {
      const result = actions
        ? await actions.test(key.id)
        : await Promise.resolve({
            ok: key.status !== "invalid",
            key: { ...key, lastCheckedAt: "방금" },
            error: key.lastError ?? undefined,
            note: "",
          });
      if (!provided)
        update((current) => ({
          ...current,
          keys: current.keys.map((item) =>
            item.id === key.id ? result.key : item,
          ),
        }));
      if (!result.ok) {
        await actions?.refresh();
        throw new Error(result.error ?? "연결 확인에 실패했습니다.");
      }
      setNotice(
        result.note || "연결을 확인하고 모델 목록을 다시 가져왔습니다.",
      );
    });
  const toggleKey = (key: AiKey) =>
    void mutate(
      key.id,
      async () => {
        const status = key.status === "disabled" ? "active" : "disabled";
        await actions?.patch(key.id, { status });
        if (!provided)
          update((current) => ({
            ...current,
            keys: current.keys.map((item) =>
              item.id === key.id ? { ...item, status } : item,
            ),
          }));
      },
      () =>
        setNotice(
          key.status === "disabled"
            ? "키를 다시 사용합니다."
            : "키 사용을 중지했습니다.",
        ),
    );
  const remove = () =>
    selected &&
    void mutate(
      "dialog",
      async () => {
        await actions?.remove(selected.id);
        if (!provided)
          update((current) => ({
            ...current,
            keys: current.keys.filter((key) => key.id !== selected.id),
          }));
      },
      () => {
        setDialog("");
        setNotice("키를 삭제했습니다. 호출 이력은 유지됩니다.");
      },
    );
  const totals = view.keys.reduce(
    (total, key) => ({
      calls: total.calls + key.month.calls,
      tokens: total.tokens + key.month.tokens,
      cost: total.cost + key.month.costUsd,
    }),
    { calls: 0, tokens: 0, cost: 0 },
  );
  const defaultInvalid = !!view.models.default && !view.models.defaultValid;
  const currentKey = selected
    ? (view.keys.find((key) => key.id === selected.id) ?? selected)
    : null;
  return (
    <div className="lai-page lai-key-settings" data-ai-keys>
      <SummaryMetrics
        columns={4}
        label="AI 키 요약"
        items={[
          {
            ...summaryActions?.keys,
            label: "등록 키",
            value: view.keys.length,
            note: view.keys.length
              ? `사용 중 ${view.keys.filter((key) => key.status === "active").length} · 무효 ${view.keys.filter((key) => key.status === "invalid").length}`
              : "먼저 API 키를 등록하세요",
            icon: <KeyRound />,
            tone: view.keys.length ? "blue" : "amber",
          },
          {
            ...summaryActions?.models,
            label: "사용 가능 모델",
            value: view.models.allowed.length,
            note: view.models.allowed.length
              ? "키마다 고른 모델"
              : view.keys.length
                ? "키마다 쓸 모델을 고르세요"
                : "키 등록 후 고를 수 있음",
            icon: <Boxes />,
            tone: view.models.allowed.length ? "teal" : "amber",
          },
          {
            ...summaryActions?.["default-model"],
            label: "기본 모델",
            value: (
              <span
                className={
                  defaultInvalid ? "lai-invalid-value" : "lai-model-value"
                }
              >
                {view.models.default || "미설정"}
              </span>
            ),
            note: view.models.defaultValid
              ? "작업에 모델을 고르지 않으면 사용"
              : defaultInvalid
                ? "고른 모델에 없음 · 다시 고르세요"
                : "아래에서 고르세요",
            icon: <CheckCircle2 />,
            tone: view.models.defaultValid ? "teal" : "coral",
          },
          {
            ...summaryActions?.usage,
            label: "이번 달 호출",
            value: totals.calls.toLocaleString(),
            note: `토큰 ${totals.tokens.toLocaleString()} · ${usd(totals.cost)}`,
            icon: <Activity />,
            tone: "neutral",
          },
        ]}
      />
      <section
        className={`lai-readiness lai-key-readiness ${view.relay.ok ? "is-ready" : "is-attention"}`}
        aria-label="API 키 준비 상태"
      >
        <span className="lai-state-icon">
          {view.relay.ok ? (
            <ShieldCheck size={21} />
          ) : (
            <CircleAlert size={21} />
          )}
        </span>
        <div>
          <strong>
            {view.relay.ok
              ? `API 키 방식 사용 가능 · ${view.relay.model}`
              : "API 키 방식을 아직 쓸 수 없습니다"}
          </strong>
          <p>
            {view.relay.ok
              ? "API 키 방식 머신의 작업은 이 키들로 실행됩니다."
              : view.relay.reason}
          </p>
        </div>
        <Badge tone={view.relay.ok ? "teal" : "amber"}>
          {view.relay.ok ? "사용 가능" : "설정 필요"}
        </Badge>
      </section>
      {!writable && (
        <p className="lai-muted">
          <Badge>보기 전용</Badge> 키 변경에는 AI API 키 관리 권한과 저장 연결이
          필요합니다.
        </p>
      )}
      {notice && (
        <p className="lai-notice" role="status">
          {notice}
        </p>
      )}
      <section
        id="ai-key-list"
        tabIndex={-1}
        className="lai-panel lai-keys-panel"
        aria-label="API 키 목록"
      >
        <header>
          <div>
            <h3>API 키</h3>
            <p>키를 등록하고, 각 키로 사용할 모델을 고르세요.</p>
          </div>
          {writable && (
            <Button size="sm" onClick={() => begin("create")} disabled={!!busy}>
              <Plus size={16} />키 등록
            </Button>
          )}
        </header>
        {!view.keys.length ? (
          <div className="lai-empty">
            <KeyRound size={28} />
            <h3>먼저 API 키를 등록하세요</h3>
            <p>등록하면 공급자에서 사용 가능한 모델 목록을 가져옵니다.</p>
          </div>
        ) : (
          <div className="lai-key-list">
            {view.keys.map((key) => (
              <article
                className={`lai-key-row ${key.status === "invalid" ? "is-invalid" : ""}`}
                key={key.id}
                aria-label={key.label}
              >
                <div className="lai-key-heading">
                  <span className="lai-key-icon">
                    <KeyRound size={19} />
                  </span>
                  <div className="lai-grow">
                    <strong>{key.label}</strong>
                    <code>{key.hint}</code>
                  </div>
                  <Badge
                    tone={
                      key.status === "active"
                        ? "teal"
                        : key.status === "invalid"
                          ? "coral"
                          : "neutral"
                    }
                  >
                    {
                      { active: "사용 중", disabled: "중지", invalid: "무효" }[
                        key.status
                      ]
                    }
                  </Badge>
                </div>
                <div className="lai-enabled-models">
                  {key.enabledModels.length ? (
                    <>
                      <span>사용 모델</span>
                      <div>
                        {key.enabledModels.map((model) => (
                          <Badge key={model}>{model}</Badge>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="lai-model-prompt">
                      {key.models.length
                        ? `가져온 모델 ${key.models.length}개 중 고르세요`
                        : "모델 목록이 없습니다. 연결 확인으로 가져오세요."}
                    </p>
                  )}
                </div>
                <dl className="lai-key-facts">
                  <div>
                    <dt>우선순위</dt>
                    <dd>
                      {key.priority}
                      <small>작은 숫자 우선</small>
                    </dd>
                  </div>
                  <div>
                    <dt>월 예산</dt>
                    <dd>
                      {key.monthlyBudgetUsd === null
                        ? "미설정"
                        : usd(key.monthlyBudgetUsd)}
                    </dd>
                  </div>
                  <div>
                    <dt>이번 달 사용</dt>
                    <dd>
                      {key.month.calls.toLocaleString()}회 ·{" "}
                      {usd(key.month.costUsd)}
                    </dd>
                  </div>
                  <div>
                    <dt>마지막 확인</dt>
                    <dd>{key.lastCheckedAt || "확인 이력 없음"}</dd>
                  </div>
                </dl>
                {key.lastError && error?.scope !== key.id && (
                  <p className="lai-error" role="alert">
                    {key.lastError}
                  </p>
                )}
                {error?.scope === key.id && (
                  <p className="lai-error" role="alert">
                    {error.message}
                  </p>
                )}
                <div className="lai-key-actions">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!!busy || !key.models.length}
                    onClick={() => begin("models", key)}
                  >
                    사용 모델 ({key.enabledModels.length}/{key.models.length})
                  </Button>
                  {writable && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!busy}
                        loading={busy === key.id}
                        onClick={() => checkKey(key)}
                      >
                        연결 확인
                      </Button>
                      <span className="lai-action-divider" />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!busy}
                        onClick={() => begin("edit", key)}
                      >
                        수정
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!busy}
                        onClick={() => toggleKey(key)}
                      >
                        {key.status === "disabled" ? "다시 사용" : "사용 중지"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="lai-danger-action"
                        disabled={!!busy}
                        onClick={() => begin("delete", key)}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      <DefaultModelPanel
        key={JSON.stringify([
          view.models.default,
          view.models.allowed,
          view.settings.effort,
          view.settings.enabled,
        ])}
        view={view}
        writable={writable}
        busy={busy}
        error={
          error?.scope === "default" || error?.scope === "relay"
            ? error.message
            : ""
        }
        onSave={(model, effort) =>
          void mutate(
            "default",
            async () => {
              await actions?.saveDefault({ model, effort });
              if (!provided)
                update((current) => ({
                  ...current,
                  settings: { ...current.settings, model, effort },
                }));
            },
            () => setNotice("기본 모델을 저장했습니다."),
          )
        }
        onToggle={() =>
          void mutate(
            "relay",
            async () => {
              await actions?.setRelayEnabled(!view.settings.enabled);
              if (!provided)
                update((current) => ({
                  ...current,
                  settings: {
                    ...current.settings,
                    enabled: !current.settings.enabled,
                  },
                }));
            },
            () =>
              setNotice(
                view.settings.enabled
                  ? "API 키 방식을 껐습니다."
                  : "API 키 방식을 켰습니다.",
              ),
          )
        }
      />
      <section className="lai-panel" aria-label="모델 단가">
        <header>
          <div>
            <h3>모델 단가</h3>
            <p>고른 모델 기준 · 100만 토큰당 USD</p>
          </div>
          {writable && (
            <Button
              size="sm"
              variant="secondary"
              disabled={!!busy || !view.models.allowed.length}
              loading={busy === "prices"}
              onClick={() =>
                void mutate(
                  "prices",
                  async () => {
                    await actions?.syncPrices();
                    if (!provided)
                      update((current) => ({
                        ...current,
                        settings: {
                          ...current.settings,
                          priceSource: Object.fromEntries(
                            current.models.allowed.map((model) => [
                              model,
                              current.settings.priceSource?.[model]?.source ===
                              "manual"
                                ? current.settings.priceSource[model]
                                : {
                                    source: "auto" as const,
                                    checkedAt: "방금",
                                  },
                            ]),
                          ),
                        },
                      }));
                  },
                  () => setNotice("모델 단가를 확인했습니다."),
                )
              }
            >
              <RefreshCw size={15} />
              지금 단가 확인
            </Button>
          )}
        </header>
        {error?.scope === "prices" && (
          <p className="lai-error" role="alert">
            {error.message}
          </p>
        )}
        {!view.models.allowed.length ? (
          <div className="lai-prices-empty">
            사용 모델을 고르면 단가를 자동으로 가져옵니다.
          </div>
        ) : (
          <div className="lai-prices-list">
            {view.models.allowed.map((model) => {
              const price = view.settings.prices[model],
                source = view.settings.priceSource?.[model];
              return (
                <article className="lai-price-row" key={model}>
                  <div className="lai-price-name">
                    <strong>{model}</strong>
                    <Badge
                      tone={source?.source === "manual" ? "neutral" : "teal"}
                    >
                      {source?.source === "manual"
                        ? "직접 입력"
                        : source
                          ? "자동 수집"
                          : "단가 미설정"}
                    </Badge>
                    {source?.checkedAt && (
                      <small>확인 {source.checkedAt}</small>
                    )}
                  </div>
                  {price ? (
                    <dl>
                      <div>
                        <dt>입력</dt>
                        <dd>{usd(price.input)}</dd>
                      </div>
                      <div>
                        <dt>캐시 입력</dt>
                        <dd>{usd(price.cachedInput)}</dd>
                      </div>
                      <div>
                        <dt>출력</dt>
                        <dd>{usd(price.output)}</dd>
                      </div>
                    </dl>
                  ) : (
                    <p>단가 없음 · 호출은 토큰만 기록됩니다.</p>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
      <Dialog
        open={!!dialog}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={
          dialog === "create"
            ? "API 키 등록"
            : dialog === "edit"
              ? "API 키 수정"
              : dialog === "models"
                ? "사용 모델"
                : "API 키 삭제"
        }
        description={
          dialog === "models"
            ? `${currentKey?.label ?? "키"}에서 사용할 모델을 고릅니다.`
            : dialog === "delete"
              ? "삭제하면 키 값이 지워지며 호출 이력은 남습니다."
              : "키 이름과 우선순위, 월 예산을 설정합니다."
        }
        size={dialog === "delete" ? "default" : "wide"}
        footer={
          <DialogActions
            secondary={{
              label: dialog === "models" && !writable ? "닫기" : "취소",
              disabled: !!busy,
              onClick: close,
            }}
            primary={
              writable
                ? {
                    label:
                      dialog === "create"
                        ? "연결 확인 후 등록"
                        : dialog === "edit"
                          ? "키 설정 저장"
                          : dialog === "models"
                            ? "사용 모델 저장"
                            : "키 삭제",
                    disabled: !!busy,
                    "aria-busy": busy === "dialog",
                    tone: dialog === "delete" ? "danger" : "default",
                    onClick: () => {
                      if (dialog === "create" || dialog === "edit") saveKey();
                      else if (dialog === "models") saveModels();
                      else if (dialog === "delete") remove();
                    },
                  }
                : undefined
            }
          />
        }
      >
        <div className="lai-stack">
          {error?.scope === "dialog" && (
            <p className="lai-error" role="alert">
              {error.message}
            </p>
          )}
          {(dialog === "create" || dialog === "edit") && (
            <>
              <Field
                label="키 이름"
                value={label}
                disabled={!!busy}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="예: 운영 기본 키"
              />
              {dialog === "create" && (
                <>
                  <Field
                    label="API 키"
                    type="password"
                    autoComplete="new-password"
                    value={secret}
                    disabled={!!busy}
                    onChange={(event) => setSecret(event.target.value)}
                    placeholder="키 값을 붙여넣으세요"
                  />
                  <p className="lai-muted lai-footnote">
                    등록할 때만 입력합니다. 저장 후에는 일부 글자만 표시됩니다.
                  </p>
                </>
              )}
              <div className="lai-form-grid">
                <Field
                  label="우선순위"
                  type="number"
                  min={0}
                  value={priority}
                  disabled={!!busy}
                  onChange={(event) => setPriority(event.target.value)}
                />
                <Field
                  label="월 예산 (USD, 선택)"
                  type="number"
                  min={0}
                  step="any"
                  value={budget}
                  disabled={!!busy}
                  onChange={(event) => setBudget(event.target.value)}
                  placeholder="미설정"
                />
              </div>
              <p className="lai-muted lai-footnote">
                우선순위가 낮은 숫자의 키를 먼저 사용합니다.
              </p>
            </>
          )}
          {dialog === "models" && currentKey && (
            <>
              {(currentKey.modelsSource === "builtin" || modelNote) && (
                <p className="lai-warning">
                  {modelNote ||
                    "이 키에는 모델 읽기 권한이 없어 Codex 기본 모델 목록을 표시합니다."}
                </p>
              )}
              <div className="lai-inline">
                <strong>{currentKey.label}</strong>
                <Badge>
                  {checked.length}개 선택 / {currentKey.models.length}개
                </Badge>
              </div>
              {currentKey.modelsSource === null && (
                <p className="lai-warning">
                  모델 목록의 출처를 아직 확인하지 못했습니다. 연결 확인으로
                  목록을 갱신해 주세요.
                </p>
              )}
              <p className="lai-muted lai-footnote">
                이 키로 사용할 모델을 고르세요. 기본 모델은 아래 기본 모델
                영역에서 설정합니다.
              </p>
              <div className="lai-model-checklist">
                {currentKey.models.map((model) => (
                  <div
                    className={checked.includes(model) ? "is-selected" : ""}
                    key={model}
                  >
                    <Checkbox
                      label={model}
                      checked={checked.includes(model)}
                      disabled={!!busy || !writable}
                      onChange={(on) =>
                        setChecked((items) =>
                          on
                            ? [...items, model]
                            : items.filter((item) => item !== model),
                        )
                      }
                    />
                    {view.models.default === model && (
                      <Badge tone="teal">기본 모델</Badge>
                    )}
                  </div>
                ))}
              </div>
              {!currentKey.models.length && (
                <p className="lai-warning">
                  모델 목록이 없습니다. 키의 연결 상태를 확인해 주세요.
                </p>
              )}
            </>
          )}
          {dialog === "delete" && (
            <p className="lai-delete-copy">
              <strong>{selected?.label}</strong> 키를 삭제할까요?
              <br />이 키로만 고른 모델은 기본 모델에서 다시 선택해야 할 수
              있습니다.
            </p>
          )}
        </div>
      </Dialog>
    </div>
  );
}

function DefaultModelPanel({
  view,
  writable,
  busy,
  error,
  onSave,
  onToggle,
}: {
  view: AiKeysView;
  writable: boolean;
  busy: string;
  error: string;
  onSave: (model: string, effort: string) => void;
  onToggle: () => void;
}) {
  const [model, setModel] = useState(
    view.models.defaultValid ? view.models.default : "",
  );
  const [effort, setEffort] = useState(view.settings.effort);
  const locked = !writable || !view.keys.length || !view.models.allowed.length;
  const unavailable = !view.keys.length
    ? "먼저 API 키를 등록하세요"
    : !view.models.allowed.length
      ? "키마다 쓸 모델을 고르세요"
      : "";
  const invalid = !!view.models.default && !view.models.defaultValid;
  return (
    <section
      id="ai-default-model"
      tabIndex={-1}
      className="lai-panel lai-default-panel"
      aria-label="기본 모델 설정"
    >
      <header>
        <div>
          <h3>기본 모델</h3>
          <p>작업에서 모델을 따로 고르지 않으면 사용합니다.</p>
        </div>
      </header>
      {unavailable && <p className="lai-warning">{unavailable}</p>}
      {invalid && (
        <p className="lai-error">
          고른 모델에 없음 · 다시 고르세요. 기존 기본 모델:{" "}
          {view.models.default}
        </p>
      )}
      <div className="lai-default-fields">
        <Select
          label="기본 모델"
          value={model}
          disabled={locked || !!busy}
          onChange={(event) => setModel(event.target.value)}
        >
          <option value="">선택하세요</option>
          {view.models.allowed.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <Select
          label="추론 강도"
          value={effort}
          disabled={locked || !!busy}
          onChange={(event) => setEffort(event.target.value)}
        >
          {["", "minimal", "low", "medium", "high", "xhigh"].map((option) => (
            <option key={option} value={option}>
              {
                (
                  {
                    "": "Codex 기본",
                    minimal: "최소",
                    low: "낮음",
                    medium: "보통",
                    high: "높음",
                    xhigh: "매우 높음",
                  } as Record<string, string>
                )[option]
              }
            </option>
          ))}
        </Select>
        {writable && (
          <Button
            disabled={locked || !model || !!busy}
            loading={busy === "default"}
            onClick={() => onSave(model, effort)}
          >
            기본 모델 저장
          </Button>
        )}
      </div>
      {error && (
        <p className="lai-error" role="alert">
          {error}
        </p>
      )}
      <div className="lai-relay-toggle">
        <div>
          <strong>API 키 방식</strong>
          <p>
            {view.settings.enabled
              ? "머신에서 API 키 방식으로 실행할 수 있습니다."
              : "API 키 방식의 새 실행을 받지 않습니다."}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={view.settings.enabled}
          aria-label="API 키 방식"
          className="lai-toggle"
          disabled={!writable || !!busy}
          onClick={onToggle}
        >
          <span />
          <b>{view.settings.enabled ? "켜짐" : "꺼짐"}</b>
        </button>
      </div>
    </section>
  );
}
